<script lang="ts" setup>
import { computed } from "vue";
import { useTranslations } from "@/composables/useTranslations";

const { t } = useTranslations();

const props = defineProps<{
    totalDomains: number;
    totalActiveDomains: number;
    totalInactiveDomains: number;
    emptyDomainsSlots: number;
    totalEmailsReported: number;
}>();

const tt = computed(() => ({
    domainsSlots: t("vue.domains_slots_tooltip"),
    activeDomains: t("vue.active_domains_tooltip"),
    inactiveDomains: t("vue.inactive_domains_tooltip"),
    emptySlots: t("vue.empty_slots_tooltip"),
}));
</script>

<template>
    <div class="domains-overview">
        <!-- LEFT BIG CARD -->
        <div class="stat-card left-big">
            <span class="stat-value">{{ totalDomains }}</span>
            <span class="stat-label">
                {{ t("vue.domains_slots") }}
                <button
                    type="button"
                    class="info-tooltip"
                    :data-tooltip="tt.domainsSlots"
                    :aria-label="tt.domainsSlots"
                    tabindex="0"
                >
                    <svg viewBox="0 0 20 20" aria-hidden="true">
                        <circle cx="10" cy="10" r="9"></circle>
                        <path d="M10 15v-5"></path>
                        <circle cx="10" cy="6.5" r="1"></circle>
                    </svg>
                </button>
            </span>
        </div>

        <!-- RIGHT BIG CARD WITH 3 MINI STATS -->
        <div class="stat-card right-big">
            <div class="mini-stats">
                <div class="mini-card">
                    <span class="stat-value">{{ totalActiveDomains }}</span>
                    <span class="stat-label">
                        {{ t("vue.active_domains") }}
                        <button
                            type="button"
                            class="info-tooltip"
                            :data-tooltip="tt.activeDomains"
                            :aria-label="tt.activeDomains"
                            tabindex="0"
                        >
                            <svg viewBox="0 0 20 20" aria-hidden="true">
                                <circle cx="10" cy="10" r="9"></circle>
                                <path d="M10 15v-5"></path>
                                <circle cx="10" cy="6.5" r="1"></circle>
                            </svg>
                        </button>
                    </span>
                </div>

                <div class="mini-card">
                    <span class="stat-value">{{ totalInactiveDomains }}</span>
                    <span class="stat-label">
                        {{ t("vue.inactive_domains") }}
                        <button
                            type="button"
                            class="info-tooltip"
                            :data-tooltip="tt.inactiveDomains"
                            :aria-label="tt.inactiveDomains"
                            tabindex="0"
                        >
                            <svg viewBox="0 0 20 20" aria-hidden="true">
                                <circle cx="10" cy="10" r="9"></circle>
                                <path d="M10 15v-5"></path>
                                <circle cx="10" cy="6.5" r="1"></circle>
                            </svg>
                        </button>
                    </span>
                </div>

                <div class="mini-card">
                    <span class="stat-value">{{ emptyDomainsSlots }}</span>
                    <span class="stat-label">
                        {{ t("vue.empty_slots") }}
                        <button
                            type="button"
                            class="info-tooltip"
                            :data-tooltip="tt.emptySlots"
                            :aria-label="tt.emptySlots"
                            tabindex="0"
                        >
                            <svg viewBox="0 0 20 20" aria-hidden="true">
                                <circle cx="10" cy="10" r="9"></circle>
                                <path d="M10 15v-5"></path>
                                <circle cx="10" cy="6.5" r="1"></circle>
                            </svg>
                        </button>
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.domains-overview {
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 16px;
    width: 100%;
}

/* CARD layout */
.stat-card {
    padding: 20px 24px;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    background: #fff;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 10px;
}

.left-big {
    text-align: center;
}

/* RIGHT BIG CARD contains 3 inline mini-cards */
.right-big {
    display: flex;
    align-items: center;
}

.mini-stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    width: 100%;
}

.mini-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
}

/* Shared typography */
.stat-value {
    color: #111827;
    font-size: 32px;
    font-weight: 600;
}

.stat-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #6b7280;
    text-transform: uppercase;
    font-size: 12px;
    font-weight: 600;
}

/* Domains overview */
.domains-overview {
    display: grid;
    grid-template-columns: 1fr 3fr;
    gap: 16px;
    width: 100%;
}

/* LEFT */
.left-block .stat-card {
    height: 100%;
}

/* RIGHT – 3 cards inline inside one big card wrapper */
.right-block {
    display: flex;
    align-items: stretch;
}

.right-inner {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    width: 100%;
}

/* CARD STYLES */
.stat-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;

    padding: 18px 24px;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    background: #fff;
}

.stat-value {
    color: #111827;
    font-size: 32px;
    font-weight: 600;
    line-height: 1;
}

.stat-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #6b7280;
    text-transform: uppercase;
    font-size: 12px;
    font-weight: 600;
}
</style>
